import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import StandingsTeamCell from './standings-team-cell';
import StatDelta from '../stat-delta';
import DensityToggle from '../density-toggle';

const inc = (i) => i + 1;
// KDA direction is derived from real data: a ratio >= 1 reads positive.
const kdaDir = (kda) => (Number(kda) >= 1 ? 'up' : 'down');

// Tournament standings as a single responsive, semantic <table>.
//
// Phase 4 (data-table upgrades): replaces the old duplicated desktop + hardcoded
// mobile markup with ONE real-data table that has a sticky header, a frozen
// team column on horizontal scroll, right-aligned tabular numerics, and mobile
// column pruning (secondary stats collapse behind a per-row expand). All team
// figures (W/L/K/D/A/KDA) come from the tournament GraphQL query.
export default class TournamentStandings extends Component {
  @tracked expanded = new Set();

  get teams() {
    return this.args.teams ?? this.args.tournament?.teams ?? [];
  }

  isExpanded = (i) => this.expanded.has(i);

  toggle = (i) => {
    const next = new Set(this.expanded);
    if (next.has(i)) next.delete(i);
    else next.add(i);
    this.expanded = next;
  };

  <template>
    <div class='widget-item'>
      <div class='standings-header'>
        <div class='section-title-wrap violet small-space'>
          <h2 class='section-title medium'>Tournament Standings</h2>
          <div class='section-title-separator'></div>
        </div>
        <DensityToggle />
      </div>

      <div class='esports-standings-scroll'>
        <table class='esports-standings'>
          <thead>
            <tr>
              <th scope='col' class='col-team'>Team</th>
              <th scope='col' class='num'>W</th>
              <th scope='col' class='num'>L</th>
              <th scope='col' class='num col-secondary'>Kills</th>
              <th scope='col' class='num col-secondary'>Deaths</th>
              <th scope='col' class='num col-secondary'>Assists</th>
              <th scope='col' class='num'>KDA</th>
              <th scope='col' class='num col-secondary'>Record</th>
              <th scope='col' class='col-expand'>
                <span class='visually-hidden'>More stats</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {{#each this.teams as |team i|}}
              <tr>
                <th scope='row' class='col-team'>
                  <StandingsTeamCell
                    @position={{inc i}}
                    @logo={{team.logo.url}}
                    @name={{team.name}}
                    @country={{team.country}}
                    @alt={{team.name}}
                  />
                </th>
                <td class='num'>{{team.wins}}</td>
                <td class='num'>{{team.losses}}</td>
                <td class='num col-secondary'>{{team.kills}}</td>
                <td class='num col-secondary'>{{team.deaths}}</td>
                <td class='num col-secondary'>{{team.assists}}</td>
                <td class='num'>
                  <StatDelta @direction={{kdaDir team.kda}} @value={{team.kda}} />
                </td>
                <td class='num col-secondary'>{{team.wins}}–{{team.losses}}</td>
                <td class='col-expand'>
                  <button
                    type='button'
                    class='standings-expand'
                    aria-expanded='{{if (this.isExpanded i) "true" "false"}}'
                    aria-label='Toggle stats for {{team.name}}'
                    {{on 'click' (fn this.toggle i)}}
                  >
                    <svg class='arrow-icon'><use href='#svg-arrow-medium'></use></svg>
                  </button>
                </td>
              </tr>
              {{#if (this.isExpanded i)}}
                <tr class='standings-detail'>
                  <td colspan='9'>
                    <dl class='standings-detail-grid'>
                      <div><dt>Kills</dt><dd>{{team.kills}}</dd></div>
                      <div><dt>Deaths</dt><dd>{{team.deaths}}</dd></div>
                      <div><dt>Assists</dt><dd>{{team.assists}}</dd></div>
                      <div><dt>Record</dt><dd>{{team.wins}}–{{team.losses}}</dd></div>
                    </dl>
                  </td>
                </tr>
              {{/if}}
            {{else}}
              <tr>
                <td class='esports-standings-empty' colspan='9'>
                  No standings available yet.
                </td>
              </tr>
            {{/each}}
          </tbody>
        </table>
      </div>
    </div>
  </template>
}
